<template>
  <section :class="$style.sectionRoot">
    <header :class="$style.sectionHeader">
      LDAP Configuration
    </header>
    <div :class="$style.sectionContent">
      <CheckboxWithLabel
        :class="$style.rowSpacing"
        label="Configure Samba Authentication"
        name="authenticationEnabled"
        :preventDefault="true"
        :tooltip="tooltips.sambaAuthentication"
        :value="authenticationEnabled"
        @change="handleSambaAuthenticationChange"
      />
      <transition
        :name="$style.sambaForm"
      >
        <form
          v-if="authenticationEnabled"
          :class="$style.sambaForm"
        >
          <InputValidator
            :config="{
              attribute: 'name',
              dataLpignore: 'true',
              label: '* Workgroup',
              name: 'name',
              validate: 'required',
              value: name,
            }"
            :tooltip="tooltips.workgroup"
            @InputValidatorModel="handleFormFieldChange"
          />
          <InputValidator
            :config="{
              attribute: 'sid',
              dataLpignore: 'true',
              label: '* SID',
              name: 'sid',
              validate: 'required|max:64|sidValidation',
              value: sid,
            }"
            :tooltip="tooltips.sid"
            @InputValidatorModel="handleFormFieldChange"
          />
          <DropDown
            v-validate="'required'"
            attribute="sambaServiceUser"
            emptyOptionText="Please first designate at least one user as an LDAP Bind DN"
            :includeEmptyOption="ldapBindDns.length === 0"
            label="Samba Service Account"
            name="Samba Service Account"
            optionLabelKey="username"
            :options="ldapBindDns"
            optionValueKey="id"
            :placeholder="sambaServiceUserPlaceHolder"
            :required="true"
            :tooltip="tooltips.sambaServiceUser"
            :value="sambaServiceUser && sambaServiceUser.id"
            @input="handleFormFieldChange"
          />
          <transition
            :name="$style.sambaServiceUserDn"
          >
            <InputValidator
              v-if="sambaServiceUser"
              :config="{
                label: 'Samba Service Account DN',
                name: 'Samba Service Account DN',
                readonly: true,
                value: sambaServiceUserDn,
              }"
            />
          </transition>
          <KnowledgeBaseArticle
            :class="$style.rowSpacing"
            :infoText="kbArticle.infoText"
            :kbLink="kbArticle.kbLink"
          />
        </form>
      </transition>
    </div>
  </section>
</template>
<script>
import * as Constants from 'client/util/Constants';
import { mapActions, mapState } from 'vuex';
import CheckboxWithLabel from 'client/components/FormValidation/CheckboxWithLabel';
import DropDown from 'client/components/FormValidation/DropDown.vue';
import InputValidator from 'client/components/FormValidation/InputValidator.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import KnowledgeBaseArticle from 'client/components/FormValidation/KnowledgeBaseArticle.vue';
import LDAPDistinguishedNameMixin from 'client/systemusers/UserDetails/mixins/LDAPDistinguishedNameMixin';
import ModalEventBus from 'client/components/Modal/ModalEventBus';

const {
  ItemTypeConsts,
  StoreModelMap,
} = Constants;
const { samba } = ItemTypeConsts;
const sambaModel = StoreModelMap[samba];

export default {
  name: 'SambaAuthentication',

  inject: ['$validator'],

  components: {
    CheckboxWithLabel,
    DropDown,
    InputValidator,
    KnowledgeBaseArticle,
  },

  mixins: [
    LDAPDistinguishedNameMixin,
  ],

  data() {
    return {
      kbArticle: {
        infoText: 'To grant access to a Samba resource, please select Enable Samba Authentication on at least one User Group. Read our',
        kbLink: KbArticleUrls.enablingSambaSupportWithLdap,
      },
      tooltips: {
        sambaAuthentication: {
          text: 'Samba Configurations within JumpCloud add necessary authentication attributes to the JumpCloud LDAP for those user groups with Samba authentication enabled.',
          kbLink: KbArticleUrls.enablingSambaSupportWithLdap,
          kbLinkText: 'Read the Knowledge Base',
        },
        sambaServiceUser: {
          text: 'The Samba Service Account is a high security account created to securely facilitate your file share configurations. This account will also have LDAP Bind DN privileges.',
        },
        sid: {
          text: 'A default SID has been created for your organization by JumpCloud. You may replace it with a custom value.',
          kbLink: KbArticleUrls.enablingSambaSupportWithLdap,
          kbLinkText: 'Read the Knowledge Base',
        },
        workgroup: {
          text: 'Change the Workgroup value to the Workgroup value defined in your Samba server configuration.',
        },
      },
    };
  },

  computed: {
    ...mapState(sambaModel, {
      authenticationEnabled: state => state.item.authenticationEnabled,
      ldapBindDns: state => state.item.ldapBindDns,
      name: state => state.item.name,
      sambaServiceUser: state => state.item.sambaServiceUser,
      sid: state => state.item.sid,
    }),

    sambaServiceUserDn() {
      if (this.sambaServiceUser) {
        return this.ldapDN(this.sambaServiceUser.username);
      }

      return '';
    },

    sambaServiceUserPlaceHolder() {
      if (this.ldapBindDns.length > 0) {
        return 'Select LDAP Bind DN for Samba Service Account';
      }
      return 'Please first designate at least one user as an LDAP Bind DN';
    },
  },

  beforeMount() {
    this.$validator.extend('sidValidation', {
      getMessage: 'Invalid SID format. A SID must follow a specfic format. Please refer to the Knowledge Base.',
      validate: this.$options.methods.validateSid,
    });
  },

  methods: {
    ...mapActions(sambaModel, [
      'updateItem',
    ]),

    handleFormFieldChange(value, key) {
      if (key === 'sambaServiceUser') {
        const sambaServiceUser = this.ldapBindDns.find(user => user.id === value);
        this.updateItem({ sambaServiceUser });
      } else {
        this.updateItem({ [key]: value });
      }
    },

    handleSambaAuthenticationChange(checked) {
      if (checked === false) {
        const componentProps = {
          mdTemplate: 'Removing Samba Configuration will delete all domain values, the Samba Service Account, and authentication you have enabled on JumpCloud\'s LDAP.\n\nTo disable authentication without removing your configuration, de-select "Enable Samba Authentication" on all user groups.',
          cancelText: 'cancel',
          continueText: 'Remove Samba Configuration',
          handleCancel: () => ModalEventBus.close(),
          handleContinue: () => {
            this.updateItem({ authenticationEnabled: checked });
            ModalEventBus.close();
          },
          headerText: 'Remove Samba Configuration',
          kbLink: KbArticleUrls.enablingSambaSupportWithLdap,
          mainActionAttention: true,
          showClose: true,
        };

        ModalEventBus.open({
          isLargeModal: false,
          component: 'ConfirmationModal',
          componentProps,
        });
      } else {
        this.updateItem({ authenticationEnabled: checked });
      }
    },

    validateSid(value = '') {
      return !!value.match(/^S-\d-(\d+-){1,14}\d+$/);
    },
  },
};
</script>
<style module scoped>
@import '../../components/FormValidation/css/formFields.css';
@import '../../css/sections.css';

@value (slowEaseFade, transformTransition) from '~css/transitions.css';

.sambaForm {
  max-height: 750px;
  padding-bottom: 20px;
}

.sambaForm-enter-active,
.sambaDorm-leave-active {
  transition: slowEaseFade;
}

.sambaForm-enter,
.sambaForm-leave-to {
  max-height: 0;
  opacity: 0;
}

.sambaServiceUserDn {
  max-height: 100px;
}

.sambaServiceUserDn-enter-active,
.sambaServiceUserDn-leave-active {
  transition: slowEaseFade;
}

.sambaServiceUserDn-enter,
.sambaServiceUserDn-leave-to {
  max-height: 0;
  opacity: 0;
}

p.rowSpacing {
  margin-top: 20px;
}
</style>
